<template>
  <div class="shipment-attachments">
    <div v-if="canUpload" class="attachment-toolbar">
      <el-upload
        action="#"
        :show-file-list="false"
        :multiple="true"
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
      >
        <el-button type="primary" size="small" icon="el-icon-upload2" :loading="uploading">
          上传{{ categoryLabel }}
        </el-button>
      </el-upload>
      <span class="attachment-tip">支持 PDF、Word、Excel、图片及压缩包，单文件不超过 50MB</span>
    </div>

    <el-table
      v-loading="loading"
      :data="attachments"
      border
      size="small"
      empty-text="暂无附件"
      class="attachment-table"
    >
      <el-table-column prop="originalName" label="附件名称" min-width="180" show-overflow-tooltip />
      <el-table-column label="大小" width="90" align="center">
        <template slot-scope="scope">{{ formatFileSize(scope.row.fileSize) }}</template>
      </el-table-column>
      <el-table-column prop="uploaderName" label="上传人" width="110" align="center">
        <template slot-scope="scope">{{ scope.row.uploaderName || '--' }}</template>
      </el-table-column>
      <el-table-column label="上传时间" width="170" align="center">
        <template slot-scope="scope">{{ formatDateTime(scope.row.uploadTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="130" align="center">
        <template slot-scope="scope">
          <el-button v-permission="'orders:order-shipment:download-attachment'" type="text" size="mini" icon="el-icon-download" @click="handleDownload(scope.row)">
            下载
          </el-button>
          <el-button
            v-if="canDelete(scope.row)"
            type="text"
            size="mini"
            class="delete-button"
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import {
  deleteShipmentAttachment,
  downloadShipmentAttachment,
  getShipmentAttachments,
  uploadShipmentAttachment
} from '@/api/modules/shipment-attachment'
import { getUserInfo } from '@/utils/auth'

const MAX_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: 'ShipmentAttachments',
  props: {
    logisticsId: {
      type: Number,
      required: true
    },
    category: {
      type: String,
      required: true
    },
    categoryLabel: {
      type: String,
      default: '附件'
    },
    canUpload: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      attachments: [],
      loading: false,
      uploading: false
    }
  },
  watch: {
    logisticsId: {
      immediate: true,
      handler(value) {
        if (value) this.loadAttachments()
      }
    },
    category() {
      if (this.logisticsId) this.loadAttachments()
    }
  },
  methods: {
    async loadAttachments() {
      if (!this.logisticsId) return
      this.loading = true
      try {
        const response = await getShipmentAttachments(this.logisticsId, this.category)
        this.attachments = response.data && response.data.success
          ? response.data.data || []
          : []
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '附件加载失败'))
      } finally {
        this.loading = false
      }
    },
    beforeUpload(file) {
      if (file.size > MAX_FILE_SIZE) {
        this.$message.error('单个附件不能超过 50MB')
        return false
      }
      return true
    },
    async handleUpload(options) {
      this.uploading = true
      const formData = new FormData()
      formData.append('file', options.file)
      formData.append('logisticsInfoId', this.logisticsId)
      formData.append('category', this.category)
      try {
        const response = await uploadShipmentAttachment(formData)
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : '附件上传失败')
        }
        options.onSuccess(response.data)
        this.$message.success('附件上传成功')
        await this.loadAttachments()
        this.$emit('change')
      } catch (error) {
        options.onError(error)
        this.$message.error(this.getErrorMessage(error, '附件上传失败'))
      } finally {
        this.uploading = false
      }
    },
    async handleDownload(attachment) {
      try {
        const response = await downloadShipmentAttachment(attachment.id)
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = attachment.originalName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '附件下载失败'))
      }
    },
    async handleDelete(attachment) {
      try {
        await this.$confirm(`确定删除附件“${attachment.originalName}”吗？`, '提示', {
          type: 'warning'
        })
        const response = await deleteShipmentAttachment(attachment.id)
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : '附件删除失败')
        }
        this.$message.success('附件删除成功')
        await this.loadAttachments()
        this.$emit('change')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(this.getErrorMessage(error, '附件删除失败'))
        }
      }
    },
    canDelete(attachment) {
      if (!this.canUpload) return false
      const userInfo = getUserInfo() || {}
      const currentUserId = userInfo.userId || userInfo.id || userInfo.user_id
      return currentUserId != null && Number(currentUserId) === Number(attachment.uploaderId)
    },
    formatFileSize(size) {
      if (size == null) return '--'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    formatDateTime(value) {
      if (!value) return '--'
      return String(value).replace('T', ' ').substring(0, 19)
    },
    getErrorMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || error.message || fallback
    }
  }
}
</script>

<style scoped>
.attachment-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.attachment-tip {
  color: #909399;
  font-size: 12px;
}

.attachment-table {
  width: 100%;
}

.delete-button {
  color: #f56c6c;
}
</style>
